import React, { useEffect, useState } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, Typography,Chip, Checkbox, Table, TableHead, TableBody, TableRow, TableCell, Avatar, TableContainer, Paper, Box
} from '@mui/material';
import { linkAssessmentService, getLinkedAssessmentService } from '../../../services/client.service';
import { TOAST_TYPES } from "../../../utils/constants";
import { toast } from "../../../utils/utils";
import ConfirmationDialog from './ConfirmationDialog.comp';
const LinkAssessmentDialog = ({assessments, open, onSubmit, loading, onClose, orgName, orgCode, linkedAssessmentCodes = [], linkingLoading = false, isSchool = false, targetNoun,}) => {
    const orgLabel = targetNoun || (isSchool ? 'school' : 'client');
    const [selectedCodes, setSelectedCodes] = useState([]);
    const [showFirstConfirmation, setShowFirstConfirmation] = useState(false);
    const [showSecondConfirmation, setShowSecondConfirmation] = useState(false);

    useEffect(() => {
        if (open) {
            setSelectedCodes([]);
            setShowFirstConfirmation(false);
            setShowSecondConfirmation(false);
        }
    }, [open]);

    const handleToggle = (code) => {
        setSelectedCodes(prev =>
            prev.includes(code) ? prev.filter(c => c !== code) : [...prev, code]
        );
    };

    const handleSubmit = () => {
        // Show first confirmation
        setShowFirstConfirmation(true);
    };

    const handleFirstConfirm = () => {
        setShowFirstConfirmation(false);
        // Show second confirmation
        setShowSecondConfirmation(true);
    };

    const handleSecondConfirm = () => {
        setShowSecondConfirmation(false);
        // Actually call the API
        onSubmit(orgCode, selectedCodes);
    };

    const handleCloseFirstConfirmation = () => {
        setShowFirstConfirmation(false);
    };

    const handleCloseSecondConfirmation = () => {
        setShowSecondConfirmation(false);
    };

    const linked = assessments.filter(a => linkedAssessmentCodes.includes(a.code));
    const unlinked = assessments.filter(a => !linkedAssessmentCodes.includes(a.code));

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: '24px',
                    overflow: 'hidden',
                    boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
                }
            }}
            BackdropProps={{
                style: { backgroundColor: 'rgba(0, 0, 0, 0.4)' }
            }}
        >
            <DialogTitle sx={{ color: '#000000', fontSize: '22px', fontWeight: 800, textAlign: 'center', pt: 4, pb: 0 }}>
                Link Standard Assessments
            </DialogTitle>
            <Box sx={{ display: 'flex', justifyContent: 'center', pb: 1 }}>
                <Box sx={{
                    display: 'inline-block', backgroundColor: 'rgba(224, 69, 53, 0.08)', color: '#E04535',
                    fontSize: '13px', fontWeight: 700, px: 1.5, py: 0.4, borderRadius: '20px',
                }}>
                    {orgName}
                </Box>
            </Box>
            <DialogContent sx={{ px: 4 }}>
                {linkingLoading  ? (
                    <Typography>Loading assessments...</Typography>
                ) : (
                    <>
                        <Typography sx={{ textAlign: 'center', fontSize: '15px', color: '#666', mb: 3 }}>
                            Select the assessment(s) that you would like to link to this {orgLabel}.
                        </Typography>
                        <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #eee', borderRadius: '16px' }}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 800, color: '#444', borderBottom: '2px solid #eee', backgroundColor: '#f8f9fa' }}>Assessment name</TableCell>
                                        <TableCell sx={{ fontWeight: 800, color: '#444', borderBottom: '2px solid #eee', backgroundColor: '#f8f9fa' }}>Time (mins)</TableCell>
                                        <TableCell sx={{ fontWeight: 800, color: '#444', borderBottom: '2px solid #eee', backgroundColor: '#f8f9fa' }}>Select</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {/* The "Auto-link is no longer enabled." row was an internal
                                        note, removed per the PW deck. */}
                                    {[...linked, ...unlinked].map((a) => {
                                        const alreadyLinked = linkedAssessmentCodes.includes(a.code);
                                        return (
                                            <TableRow
                                                key={a.code || a.id}
                                                sx={{
                                                    backgroundColor: alreadyLinked ? 'rgba(255, 105, 0, 0.05)' : 'inherit'
                                                }}
                                            >
                                                <TableCell sx={{ display: 'flex', alignItems: 'center', borderBottom: '1px solid #eee' }}>
                                                    <Avatar
                                                        src={a.image_url}
                                                        variant="rounded"
                                                        sx={{ width: 56, height: 56, border: '1px solid #eee', borderRadius: '12px' }}
                                                    />
                                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                        <Typography
                                                            sx={{
                                                                fontWeight: 500,
                                                                lineHeight: '1.5',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                marginLeft: '15px',
                                                            }}
                                                        >
                                                            {a.full_name}
                                                        </Typography>
                                                    </Box>
                                                </TableCell>
                                                <TableCell sx={{ borderBottom: '1px solid #eee', color: '#555' }}>
                                                    <Box sx={{
                                                        display: 'inline-block', backgroundColor: 'rgba(255, 105, 0, 0.08)',
                                                        borderRadius: '6px', px: 1.25, py: 0.4,
                                                    }}>
                                                        {a.time_to_complete_minutes}
                                                    </Box>
                                                </TableCell>
                                                <TableCell align="center" sx={{ borderBottom: '1px solid #eee' }}>
                                                    {alreadyLinked ? (
                                                        <Chip
                                                            label="Already Linked"
                                                            size="small"
                                                            sx={{
                                                                backgroundColor: 'rgba(255, 105, 0, 0.1)',
                                                                color: '#FF6900',
                                                                fontWeight: 'bold',
                                                                borderRadius: '8px'
                                                            }}
                                                        />
                                                    ) : (
                                                        <Checkbox
                                                            checked={selectedCodes.includes(a.code)}
                                                            onChange={() => handleToggle(a.code)}
                                                            sx={{
                                                                color: '#FF6900',
                                                                '&.Mui-checked': { color: '#FF6900' },
                                                            }}
                                                        />
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </>
                )}
            </DialogContent>
            <DialogActions sx={{ pt: 3, pb: 4 }}>
                <Box sx={{ display: 'flex', width: '100%', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                    <Button
                        onClick={onClose}
                        disabled={loading}
                        sx={{
                            minWidth: 180,
                            px: 4,
                            py: 1.25,
                            borderRadius: '10px',
                            backgroundColor: '#fff',
                            color: '#111',
                            fontWeight: 700,
                            fontSize: '16px',
                            textTransform: 'none',
                            border: '1.5px solid #111',
                            '&:hover': { backgroundColor: '#f7f7f7', borderColor: '#111' },
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        disabled={loading || selectedCodes.length === 0}
                        sx={{
                            minWidth: 180,
                            px: 4,
                            py: 1.25,
                            borderRadius: '10px',
                            backgroundColor: '#FF6900',
                            color: '#fff',
                            fontWeight: 700,
                            fontSize: '16px',
                            textTransform: 'none',
                            boxShadow: 'none',
                            '&:hover': { backgroundColor: '#e55e00', boxShadow: 'none' },
                            '&.Mui-disabled': { backgroundColor: '#FFD0AA', color: '#fff' },
                        }}
                    >
                        {loading ? 'Linking...' : 'Submit'}
                    </Button>
                </Box>
            </DialogActions>

            {/* First Confirmation Dialog */}
            <ConfirmationDialog
                open={showFirstConfirmation}
                onClose={handleCloseFirstConfirmation}
                onConfirm={handleFirstConfirm}
                title="Confirm linking"
                message={`Do you want to link <b>${selectedCodes.length}</b> assessment(s) to <b>${orgName}?</b>`}
                confirmText="Proceed"
                cancelText="Cancel"
            />

            {/* Second Confirmation Dialog */}
            <ConfirmationDialog
                open={showSecondConfirmation}
                onClose={handleCloseSecondConfirmation}
                onConfirm={handleSecondConfirm}
                title="Are You Sure?"
                message="Are you sure? Unlinking will not be possible once linked."
                confirmText="Yes, Link Now"
                cancelText="Cancel"
                isWarning={true}
            />
        </Dialog>
    );
};

export default LinkAssessmentDialog;